import {
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalTenantId,
  setLocalUserInfo,
  getRefreshToken,
} from "@/lib/storeManager";
import {
  getGluceraUserInfo,
  setGluceraAccessToken,
  setGluceraUserInfo,
} from "@/projects/Glucera/utils/storage";
import {
  clearGluceraAuthCookies,
  getGluceraUserIdFromStorage,
  setGluceraAuthCookies,
} from "@/projects/Glucera/utils/gluceraAuthCookies";
import {
  savePWATokensReliably,
  restorePWATokensIfNeeded,
  clearPWATokenStorage,
} from "@/lib/pwaTokenStorage";
import { clearSessionValidationFlag } from "@/projects/Glucera/utils/authSessionInvalidation";

export const GLUCERA_REFRESH_KEY = "glucera_refresh_token";

/** Persist tokens to Glucera and main-app storage keys used by axios/interceptors. */
export function saveGluceraAuthSession(
  tokens: { access: string; refresh: string },
  user?: unknown
): void {
  clearSessionValidationFlag();
  setGluceraAccessToken(tokens.access);
  localStorage.setItem(GLUCERA_REFRESH_KEY, tokens.refresh);
  setLocalAccessToken(tokens.access);
  setLocalRefreshToken(tokens.refresh);

  const userId =
    user && typeof user === "object" && "id" in user
      ? String((user as { id?: string }).id ?? "")
      : getGluceraUserIdFromStorage();

  if (user) {
    setGluceraUserInfo(user);
    setLocalUserInfo(user);
  }

  const userInfoForCookie = user ?? getGluceraUserInfo();
  setGluceraAuthCookies(tokens.access, userId || null, userInfoForCookie);

  const tenantId = getTenantId();
  if (tenantId) {
    setLocalTenantId(tenantId);
  }

  // 🔒 PWA-optimized storage - save tokens reliably for installed app
  void savePWATokensReliably({
    access: tokens.access,
    refresh: tokens.refresh,
    gluceraAccess: tokens.access,
    gluceraRefresh: tokens.refresh,
  }).catch(err => console.error("❌ PWA token save failed:", err));
}

export function clearGluceraAuthSession(): void {
  clearGluceraAuthCookies();
  // Also clear PWA storage when logging out
  void clearPWATokenStorage().catch(err =>
    console.error("❌ Error clearing PWA storage:", err)
  );
}

/**
 * Restore tokens from PWA storage on app startup (if needed)
 * This ensures PWA users stay logged in after app restarts
 */
export async function restoreGluceraAuthSessionFromPWA(): Promise<boolean> {
  try {
    const restoredTokens = await restorePWATokensIfNeeded();
    if (!restoredTokens) return false;

    console.log("🔄 Restoring PWA session...");

    // Restore tokens to memory
    setLocalAccessToken(restoredTokens.accessToken);
    setLocalRefreshToken(restoredTokens.refreshToken);
    setGluceraAccessToken(restoredTokens.gluceraAccessToken);
    localStorage.setItem(GLUCERA_REFRESH_KEY, restoredTokens.gluceraRefreshToken);

    console.log("✅ PWA session restored successfully");
    return true;
  } catch (error) {
    console.error("❌ Error restoring PWA session:", error);
    return false;
  }
}

export function getGluceraRefreshToken(): string | null {
  return localStorage.getItem(GLUCERA_REFRESH_KEY) ?? getRefreshToken();
}
